import { useCallback, useEffect, useState } from 'react';
import { knowledgeAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Database, Upload, Save, Loader2 } from 'lucide-react';

export default function CapsuleKBAccessPanel({ capsuleId }) {
  const [access, setAccess] = useState({
    kb_repo_access: [],
    kb_source_access: [],
    kb_manual_files_enabled: true,
  });
  const [allRepos, setAllRepos] = useState([]);
  const [allMounts, setAllMounts] = useState([]);
  const [manualFiles, setManualFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!capsuleId) return;
    setLoading(true);
    try {
      const [accRes, reposRes, mountsRes, filesRes] = await Promise.all([
        knowledgeAPI.getCapsuleKbAccess(capsuleId),
        knowledgeAPI.listRepos(),
        knowledgeAPI.listSourceMounts(),
        knowledgeAPI.listManualFiles(capsuleId),
      ]);
      setAccess({
        kb_repo_access: accRes.data?.kb_repo_access || [],
        kb_source_access: accRes.data?.kb_source_access || [],
        kb_manual_files_enabled: accRes.data?.kb_manual_files_enabled !== false,
      });
      setAllRepos(reposRes.data?.repos || []);
      setAllMounts(mountsRes.data?.mounts || []);
      setManualFiles(filesRes.data?.files || []);
    } catch (e) {
      toast.error('Error cargando acceso KB');
    } finally {
      setLoading(false);
    }
  }, [capsuleId]);

  useEffect(() => { load(); }, [load]);

  const toggleRepo = (repoId) => {
    setAccess((prev) => {
      const exists = prev.kb_repo_access.find((r) => r.repo_id === repoId);
      if (exists) {
        return { ...prev, kb_repo_access: prev.kb_repo_access.filter((r) => r.repo_id !== repoId) };
      }
      return { ...prev, kb_repo_access: [...prev.kb_repo_access, { repo_id: repoId, include_globs: ['**/*'], exclude_globs: [] }] };
    });
  };

  const toggleMount = (mountId) => {
    setAccess((prev) => {
      const exists = prev.kb_source_access.find((m) => m.mount_id === mountId);
      if (exists) {
        return { ...prev, kb_source_access: prev.kb_source_access.filter((m) => m.mount_id !== mountId) };
      }
      return { ...prev, kb_source_access: [...prev.kb_source_access, { mount_id: mountId, include_globs: ['**/*'], exclude_globs: [] }] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await knowledgeAPI.updateCapsuleKbAccess(capsuleId, access);
      toast.success('Acceso KB guardado');
    } catch {
      toast.error('Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await knowledgeAPI.uploadManualFile(capsuleId, fd);
      toast.success('Archivo subido e indexado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo archivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!capsuleId) {
    return <p className="text-sm text-zinc-500">Selecciona una capsula para configurar acceso KB.</p>;
  }

  if (loading) return <p className="text-sm text-zinc-400">Cargando...</p>;

  return (
    <div className="space-y-4" data-testid="capsule-kb-access-panel">
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800">
        <Database className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          Define que repositorios Git y mounts locales puede usar esta capsula (techo de acceso).
          El subconjunto por agente se configura en <b>Agentes → Runtime</b> para cada agente.
          Los archivos manuales se almacenan en <span className="font-mono">/data/capsule-files</span>.
        </p>
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Repositorios Git autorizados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {allRepos.length === 0 ? (
            <p className="text-xs text-zinc-400">Sin repos en el pool. Agregalos en KB &gt; Origenes.</p>
          ) : (
            allRepos.map((r) => {
              const on = access.kb_repo_access.some((x) => x.repo_id === r.repo_id);
              return (
                <label key={r.repo_id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => toggleRepo(r.repo_id)} />
                  <span className="font-semibold">{r.repo_id}</span>
                  <span className="text-zinc-500 truncate">{r.url}</span>
                </label>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Mounts locales autorizados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {allMounts.length === 0 ? (
            <p className="text-xs text-zinc-400">Sin mounts. Declaralos en KB &gt; Origenes.</p>
          ) : (
            allMounts.map((m) => {
              const on = access.kb_source_access.some((x) => x.mount_id === m.mount_id);
              return (
                <label key={m.mount_id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => toggleMount(m.mount_id)} />
                  <span className="font-semibold">{m.label}</span>
                  <span className="text-zinc-500 font-mono">{m.subpath}</span>
                </label>
              );
            })
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Switch
          checked={access.kb_manual_files_enabled}
          onCheckedChange={(v) => setAccess((p) => ({ ...p, kb_manual_files_enabled: v }))}
        />
        <Label className="text-xs">Archivos manuales habilitados para esta capsula</Label>
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Upload className="w-4 h-4" /> Subir archivo manual
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input type="file" onChange={handleUpload} disabled={uploading || !access.kb_manual_files_enabled} className="text-xs" />
          {manualFiles.length > 0 && (
            <div className="space-y-1 mt-2">
              {manualFiles.map((f) => (
                <div key={f.file_id} className="text-xs flex justify-between border-b py-1">
                  <span>{f.filename}</span>
                  <Badge variant="outline" className="text-[9px]">{f.status || 'indexed'}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} size="sm">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar acceso KB
      </Button>
    </div>
  );
}
